import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Send } from 'lucide-react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { prospectDate } from '@/utils/prospectTime';

// Responder dentro do admin (nunca mailto): envio sempre de info@trabadju.cv, com assinatura, PDF e horários opcionais.
export const ProspectReplyButton = ({ p, onSent, size = 'sm', label = 'Responder' }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    try { const { data } = await api.get(`/admin/prospects/${p.id}/compose`); setForm(data); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/prospects/${p.id}/send-reply`, { subject: form.subject, body: form.body, attach_pdf: form.attach_pdf, include_slots: form.include_slots });
      toast.success('Email enviado de info@trabadju.cv'); setForm(null); onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Não foi possível enviar'); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <>
      <Button size={size} onClick={open} disabled={!p.email} data-testid={`prospect-reply-btn-${p.id}`}><Mail className="h-3.5 w-3.5 mr-1" />{label}</Button>
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3" data-testid="prospect-reply-dialog">
          <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-xl bg-white p-5 space-y-3">
            <h3 className="font-bold text-lg">Responder a {p.company}</h3>
            <p className="text-xs text-slate-500">De: <strong>info@trabadju.cv</strong> · Para: <strong>{form.to}</strong></p>
            <input value={form.subject} onChange={set('subject')} maxLength={200} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="prospect-reply-subject" />
            <textarea value={form.body} onChange={set('body')} rows={9} maxLength={8000} className="w-full rounded-md border border-slate-300 p-3 text-sm" data-testid="prospect-reply-body" />
            <p className="text-xs text-slate-500" data-testid="prospect-reply-signature">Assinatura: {form.signature}</p>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.attach_pdf} onChange={set('attach_pdf')} data-testid="prospect-reply-attach-pdf" />Anexar o PDF de apresentação</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.include_slots} onChange={set('include_slots')} data-testid="prospect-reply-include-slots" />Incluir 3 botões de horários livres (hora de Cabo Verde)</label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setForm(null)} data-testid="prospect-reply-cancel">Cancelar</Button>
              <Button onClick={send} disabled={busy || form.body.trim().length < 5 || form.subject.trim().length < 3} data-testid="prospect-reply-send"><Send className="h-3.5 w-3.5 mr-1" />{busy ? 'A enviar…' : 'Enviar'}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const InboundReplies = ({ p }) => {
  const last = (p.inbound_replies || []).slice(-1)[0];
  if (!last) return null;
  return (
    <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm" data-testid={`prospect-inbound-${p.id}`}>
      <p className="text-xs font-bold text-blue-800">📩 Resposta da empresa · {prospectDate(last.at)?.toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · {last.subject}</p>
      <p className="mt-1 whitespace-pre-line text-slate-800 line-clamp-6">{last.text || '(sem texto)'}</p>
      {p.inbound_replies.length > 1 && <p className="mt-1 text-xs text-slate-500">{p.inbound_replies.length} respostas no total</p>}
    </div>
  );
};

const TEMPLATE_LABELS = {
  interested: 'Interessado', preference: 'Preferência escolhida', invite_sent: 'Convite enviado', confirmed: 'Reunião confirmada',
  held: 'Reunião realizada', default: 'Outras etapas', followup: 'Seguimento automático (1 dia após reunião realizada)',
  no_show: 'Nova data — não compareceu', cancelled: 'Nova data — cancelada', reschedule: 'Nova data — remarcar',
};

export const ReplyTemplatesSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [key, setKey] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api.get('/admin/prospects/reply-templates').then((r) => setCfg(r.data)).catch(() => setFailed(true)); }, []);
  const save = async (next) => {
    try { const { data } = await api.put('/admin/prospects/reply-templates', next); setCfg(data); toast.success('Guardado'); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  if (failed) return <p className="mb-6 text-sm text-red-600" data-testid="reply-templates-error">Não foi possível carregar os modelos.</p>;
  if (!cfg) return null;
  const t = key && cfg.templates[key];
  const edit = (f) => (e) => setCfg({ ...cfg, templates: { ...cfg.templates, [key]: { ...t, [f]: e.target.value } } });
  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="reply-templates-settings">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 font-bold text-slate-900">Modelos do «Responder» por etapa</p>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={cfg.followup_enabled} onChange={(e) => save({ followup_enabled: e.target.checked, templates: {} })} data-testid="followup-toggle" />
          Seguimento pós-reunião {cfg.followup_enabled ? 'ligado' : 'desligado'}
        </label>
      </div>
      <p className="mt-1 text-xs text-slate-500">O seguimento sai 1 dia depois de marcar a reunião como realizada, com o PDF e o botão «Registar a minha empresa». Não é enviado se a empresa já estiver registada. Variáveis: {'{empresa}'}, {'{contacto}'}, {'{data}'}, {'{hora_cv}'}, {'{hora_suica}'} e {'{link_reuniao}'} (preenchidas a partir da reunião da ficha).</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {Object.keys(TEMPLATE_LABELS).map((k) => (
          <Button key={k} size="sm" variant={k === key ? 'default' : 'outline'} onClick={() => setKey(k === key ? null : k)} data-testid={`reply-template-tab-${k}`}>{TEMPLATE_LABELS[k]}</Button>
        ))}
      </div>
      {t && (
        <div className="mt-3 space-y-2">
          <input value={t.subject} onChange={edit('subject')} maxLength={200} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="reply-template-subject" />
          <textarea value={t.body} onChange={edit('body')} rows={7} maxLength={5000} className="w-full rounded-md border border-slate-300 p-3 text-sm" data-testid="reply-template-body" />
          <Button size="sm" onClick={() => save({ followup_enabled: cfg.followup_enabled, templates: { [key]: t } })} data-testid="reply-template-save">Guardar modelo</Button>
        </div>
      )}
    </div>
  );
};

export const AutoreplySettings = () => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api.get('/admin/prospects/autoreply-settings').then((r) => setCfg(r.data)).catch(() => setFailed(true)); }, []);
  const save = async (next) => {
    try { const { data } = await api.put('/admin/prospects/autoreply-settings', next); setCfg({ ...cfg, ...data }); toast.success('Guardado'); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  if (failed) return <p className="mb-6 text-sm text-red-600" data-testid="autoreply-settings-error">Não foi possível carregar a resposta automática.</p>;
  if (!cfg) return null;
  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="autoreply-settings">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 font-bold text-slate-900">Resposta automática ao «Quero saber mais»</p>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => save({ enabled: e.target.checked, subject: cfg.subject, body: cfg.body })} data-testid="autoreply-toggle" />
          {cfg.enabled ? 'Ligada' : 'Desligada'}
        </label>
        <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid="autoreply-edit-toggle">{open ? 'Fechar' : 'Editar modelo'}</Button>
      </div>
      <p className="mt-1 text-xs text-slate-500">Enviada de info@trabadju.cv com o PDF de apresentação, 3 horários livres e «Prefiro outro contacto». Um único lembrete após 3 dias sem resposta. Nunca inclui a oferta Pioneiro. Use {'{empresa}'} e {'{contacto}'}.</p>
      {open && (
        <div className="mt-3 space-y-2">
          <input value={cfg.subject} onChange={(e) => setCfg({ ...cfg, subject: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="autoreply-subject" />
          <textarea value={cfg.body} onChange={(e) => setCfg({ ...cfg, body: e.target.value })} rows={8} className="w-full rounded-md border border-slate-300 p-3 text-sm" data-testid="autoreply-body" />
          <Button size="sm" onClick={() => save({ enabled: cfg.enabled, subject: cfg.subject, body: cfg.body })} data-testid="autoreply-save">Guardar modelo</Button>
        </div>
      )}
    </div>
  );
};
